import { useEffect, useState } from "react";

// Browser-fitness measurements for onboarding (2026-10-08). Only what the
// browser can see about the device the page is open on: connection, battery
// (Chrome/Android only — iOS Safari has no Battery API), in-app browser,
// cookies, phone-vs-computer (the QR code must be scanned by the WhatsApp
// phone, so a phone cannot host the page). Unavailable = "unknown", never a
// guess. Nothing about the participant's WhatsApp phone is measurable here;
// how device health is handled before donation is a PI decision.

const nav = () => (typeof navigator !== "undefined" ? navigator : {});
const conn = () => { const n = nav(); return n.connection || n.mozConnection || n.webkitConnection || null; };

export const detectEnvironment = () => {
    const n = nav();
    const ua = n.userAgent || "";
    const uaMobile = n.userAgentData && typeof n.userAgentData.mobile === "boolean" ? n.userAgentData.mobile : null;
    const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Mac/.test(ua) && (n.maxTouchPoints || 0) > 1);
    const isAndroid = /Android/.test(ua);
    const isMobile = uaMobile !== null ? uaMobile || /iPhone|iPod/.test(ua) : /Mobi|iPhone|iPod|Android.*Mobile/.test(ua);
    // Links shared inside WhatsApp/Instagram/Facebook open in an embedded
    // webview whose session cookies and camera/QR handling are unreliable.
    const inAppBrowser = /FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Line\/|Twitter|; wv\)|Snapchat|TikTok/i.test(ua);
    return { isIOS, isAndroid, isMobile, inAppBrowser, cookiesEnabled: typeof n.cookieEnabled === "boolean" ? n.cookieEnabled : null };
};

const readNetwork = () => {
    const n = nav();
    const c = conn();
    const online = typeof n.onLine === "boolean" ? n.onLine : true;
    const effectiveType = c ? c.effectiveType || null : null;
    const downlink = c && typeof c.downlink === "number" ? c.downlink : null;
    const saveData = c ? !!c.saveData : false;
    let status = "unknown";
    if (!online) status = "bad";
    else if (effectiveType) status = ["slow-2g", "2g"].includes(effectiveType) ? "bad" : effectiveType === "3g" || saveData ? "warn" : "ok";
    else status = "ok"; // online, no detail API (iOS Safari) — treat as fine
    return { online, effectiveType, downlink, saveData, status };
};

export const useDeviceStatus = () => {
    const [network, setNetwork] = useState(readNetwork);
    const [battery, setBattery] = useState({ level: null, charging: null, status: "unknown" });
    const [env] = useState(detectEnvironment);

    useEffect(() => {
        const update = () => setNetwork(readNetwork());
        window.addEventListener("online", update);
        window.addEventListener("offline", update);
        const c = conn();
        if (c && c.addEventListener) c.addEventListener("change", update);
        const timer = setInterval(update, 15000);
        return () => {
            window.removeEventListener("online", update);
            window.removeEventListener("offline", update);
            if (c && c.removeEventListener) c.removeEventListener("change", update);
            clearInterval(timer);
        };
    }, []);

    useEffect(() => {
        let bm = null;
        let cancelled = false;
        const apply = () => {
            if (!bm) return;
            const level = typeof bm.level === "number" ? bm.level : null;
            const charging = !!bm.charging;
            let status = "unknown";
            if (level !== null) status = charging || level >= 0.4 ? "ok" : level >= 0.15 ? "warn" : "bad";
            setBattery({ level, charging, status });
        };
        const n = nav();
        if (n.getBattery) {
            n.getBattery().then((b) => {
                if (cancelled) return;
                bm = b; apply();
                b.addEventListener("levelchange", apply);
                b.addEventListener("chargingchange", apply);
            }).catch(() => {});
        }
        return () => {
            cancelled = true;
            if (bm) { bm.removeEventListener("levelchange", apply); bm.removeEventListener("chargingchange", apply); }
        };
    }, []);

    return { network, battery, env };
};
